import { fetchEventSource } from "@microsoft/fetch-event-source";
import { ChatMessageDTO, ChatQuestionDTO } from "@onetutor/shared/dto";
import { User } from "oidc-client-ts";
import { Dispatch, SetStateAction } from "react";
import { z } from "zod";
import { environment } from "../environment";

const deltaResponseSchema = z.object({
  delta: z.string(),
  citations: z.array(z.string()).optional(),
  done: z.boolean().optional(),
});

export async function receiveStream({
  courseId,
  sessionId,
  streamingMessageId,
  setIsStreaming,
  setMessages,
  setIsWaitingForFirstToken,
  userInput,
}: {
  courseId: string;
  sessionId: string;
  streamingMessageId: string;
  setIsStreaming: Dispatch<SetStateAction<boolean>>;
  setMessages: Dispatch<SetStateAction<ChatMessageDTO[]>>;
  setIsWaitingForFirstToken: Dispatch<SetStateAction<boolean>>;
  userInput: string;
}) {
  const url = `${environment.backendUrl}/course/${courseId}/chat/${sessionId}`;
  const controller = new AbortController();

  let accumulatedResponse = "";

  const oidcStorage = sessionStorage.getItem(
    `oidc.user:${environment.oidc.authority}:${environment.oidc.client_id}`,
  );
  if (!oidcStorage) {
    return;
  }

  const user = User.fromStorageString(oidcStorage);

  let committed = false;

  const commitFinalMessage = ({
    citations,
  }: {
    citations: string[] | null;
  }) => {
    if (committed || !accumulatedResponse) {
      return;
    }

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === streamingMessageId
          ? { ...msg, content: accumulatedResponse, citations }
          : msg,
      ),
    );
    setIsStreaming(false);
    committed = true;
  };

  await fetchEventSource(url, {
    signal: controller.signal,
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${user.access_token}`,
    },
    body: JSON.stringify({ content: userInput } satisfies ChatQuestionDTO),
    openWhenHidden: true,

    onmessage(event) {
      setIsWaitingForFirstToken(false);

      let parsedData: {
        delta: string;
        citations?: string[] | undefined;
        done?: boolean | undefined;
      };
      try {
        parsedData = deltaResponseSchema.parse(JSON.parse(event.data));
      } catch {
        return; // skip malformed data silently
      }

      accumulatedResponse += parsedData.delta;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingMessageId
            ? { ...msg, content: accumulatedResponse }
            : msg,
        ),
      );

      if (parsedData.done) {
        commitFinalMessage({
          citations: parsedData.citations
            ? [...new Set(parsedData.citations)]
            : null,
        });
        controller.abort();
      }
    },

    onerror() {
      setIsStreaming(false);
      controller.abort();
    },

    onclose() {
      controller.abort(); // Avoid automatic reconnect
      commitFinalMessage({ citations: null }); // ensure fallback message is committed
    },
  });
}
